import {
    CloverHostedFieldsBrowserTokenizationSessionSchema,
    loadCloverHostedFieldsSdk,
    initializeCloverHostedFieldsDocument,
    mountCloverHostedFields,
    stringifyLosslessJson,
    type CloverHostedFieldStyleSheet,
    type CloverHostedFieldsController,
    type CloverHostedFieldsDocument,
} from '@pillar-portal/api-types';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PortalHostedCardEntryProps } from '@/lib/ipg/card-entry';

// Clover draws the field text inside its own frames; these match the payment
// page's light inputs (text-sm, slate text and placeholder).
const InvoiceCloverHostedFieldStyles: CloverHostedFieldStyleSheet = {
    body: {
        margin: '0',
        padding: '0',
        background: 'transparent',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
    },
    input: {
        boxSizing: 'border-box',
        width: '100%',
        height: '38px',
        padding: '0',
        background: 'transparent',
        border: '0',
        outline: 'none',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
        fontSize: '14px',
        fontWeight: '400',
        lineHeight: '20px',
        color: '#0f172a',
    },
    'input::placeholder': {
        color: '#94a3b8',
    },
};

// Traceability: implements IPS-EINV-001
export function CloverHostedInvoiceCardEntry(
    portalHostedCardEntryProps: PortalHostedCardEntryProps
) {
    return (
        <CloverHostedInvoiceCardEntrySession
            key={stringifyLosslessJson(portalHostedCardEntryProps.delegateToken)}
            {...portalHostedCardEntryProps}
        />
    );
}

function CloverHostedInvoiceCardEntrySession({
    delegateToken,
    disabled,
    onTokenized,
    onError,
    tokenizationRequestId = 0,
    onTokenizationReadyChange,
}: PortalHostedCardEntryProps) {
    const session = CloverHostedFieldsBrowserTokenizationSessionSchema.parse(delegateToken);
    const id = useId().replaceAll(':', '');
    const cardNumberId = `clover-invoice-card-number-${id}`;
    const cardDateId = `clover-invoice-card-date-${id}`;
    const cardCvvId = `clover-invoice-card-cvv-${id}`;
    const cardPostalCodeId = `clover-invoice-card-postal-code-${id}`;
    const controllerRef = useRef<CloverHostedFieldsController | null>(null);
    const onErrorRef = useRef(onError);
    const [cardholderName, setCardholderName] = useState('');
    const cardholderNameRef = useRef(cardholderName);
    cardholderNameRef.current = cardholderName;
    const lastTokenizationRequestIdRef = useRef(tokenizationRequestId);
    onErrorRef.current = onError;
    const onTokenizationReadyChangeRef = useRef(onTokenizationReadyChange);
    onTokenizationReadyChangeRef.current = onTokenizationReadyChange;
    const onTokenizedRef = useRef(onTokenized);
    onTokenizedRef.current = onTokenized;
    const htmlIFrameElementRef = useRef<HTMLIFrameElement | null>(null);
    const [hostedDocument, setHostedDocument] = useState<Document | null>(null);

    useLayoutEffect(() => {
        const htmlIFrameElement = htmlIFrameElementRef.current;
        if (htmlIFrameElement === null) return;
        const abortController = new AbortController();
        let cloverHostedFieldsDocument: CloverHostedFieldsDocument | undefined;
        initializeCloverHostedFieldsDocument({
            htmlIFrameElement,
            sdkUrl: session.sdk_url,
            abortSignal: abortController.signal,
        })
            .then((initializedCloverHostedFieldsDocument) => {
                if (abortController.signal.aborted) {
                    initializedCloverHostedFieldsDocument.dispose();
                    return;
                }
                cloverHostedFieldsDocument = initializedCloverHostedFieldsDocument;
                setHostedDocument(initializedCloverHostedFieldsDocument.document);
            })
            .catch((error: unknown) => {
                if (!abortController.signal.aborted) {
                    onErrorRef.current(
                        error instanceof Error
                            ? error.message
                            : 'Unable to initialize Clover secure card entry.'
                    );
                }
            });
        return () => {
            abortController.abort();
            controllerRef.current = null;
            onTokenizationReadyChangeRef.current?.(false);

            cloverHostedFieldsDocument?.dispose();
        };
    }, [session.api_access_key, session.merchant_id, session.sdk_url]);

    useLayoutEffect(() => {
        if (hostedDocument === null) return;
        let active = true;
        const initialize = async (): Promise<void> => {
            await loadCloverHostedFieldsSdk(hostedDocument, session.sdk_url);
            if (!active) return;
            controllerRef.current = mountCloverHostedFields({
                document: hostedDocument,
                session,
                targets: {
                    cardNumber: `#${cardNumberId}`,
                    cardDate: `#${cardDateId}`,
                    cardCvv: `#${cardCvvId}`,
                    cardPostalCode: `#${cardPostalCodeId}`,
                },
                styles: InvoiceCloverHostedFieldStyles,
            });
            onTokenizationReadyChangeRef.current?.(true);
        };
        initialize().catch((error: unknown) => {
            if (active) {
                onErrorRef.current(
                    error instanceof Error
                        ? error.message
                        : 'Unable to load Clover secure card entry.'
                );
            }
        });
        return (): void => {
            active = false;
        };
    }, [
        hostedDocument,
        cardCvvId,
        cardDateId,
        cardNumberId,
        cardPostalCodeId,
        session.api_access_key,
        session.merchant_id,
        session.sdk_url,
    ]);

    // Pay Now asks for a token by raising tokenizationRequestId; the page then
    // quotes the token and pays with it (Alex, 2026-10-30).
    useLayoutEffect(() => {
        if (
            tokenizationRequestId === 0 ||
            tokenizationRequestId === lastTokenizationRequestIdRef.current
        ) {
            return;
        }
        lastTokenizationRequestIdRef.current = tokenizationRequestId;
        const normalizedName = cardholderNameRef.current.trim();
        if (normalizedName.length === 0) {
            onErrorRef.current('Name on card is required.');
            return;
        }
        const controller = controllerRef.current;
        let active = true;
        const tokenize = async (): Promise<void> => {
            if (controller === null) {
                throw new Error('Clover secure card entry is not ready.');
            }
            const tokenizationResult = await controller.createToken();
            if (!active || controllerRef.current !== controller) return;
            onTokenizedRef.current({
                token: tokenizationResult.token,
                tokenizationResult,
                cardholderName: normalizedName,
            });
        };
        tokenize().catch((error: unknown) => {
            if (active && controllerRef.current === controller) {
                onErrorRef.current(
                    error instanceof Error ? error.message : 'Unable to secure card details.'
                );
            }
        });
        return (): void => {
            active = false;
        };
    }, [tokenizationRequestId]);

    return (
        <div className="space-y-3">
            <label
                className="block text-sm font-medium"
                htmlFor={`clover-invoice-cardholder-name-${id}`}
            >
                Name on card
            </label>
            <input
                id={`clover-invoice-cardholder-name-${id}`}
                autoComplete="cc-name"
                disabled={disabled}
                value={cardholderName}
                onChange={(event) => setCardholderName(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <iframe
                ref={htmlIFrameElementRef}
                title="Clover secure invoice card entry"
                data-clover-owned-document=""
                inert={disabled}
                style={{ width: '100%', height: 0, border: 0, display: 'block' }}
            >
                {hostedDocument === null
                    ? null
                    : createPortal(
                          <div className="space-y-3">
                              <label className="block text-sm font-medium" htmlFor={cardNumberId}>
                                  Card number
                              </label>
                              <div
                                  id={cardNumberId}
                                  className="h-10 rounded-md border border-input bg-background px-3"
                              />
                              <label className="block text-sm font-medium" htmlFor={cardDateId}>
                                  Expiration date
                              </label>
                              <div
                                  id={cardDateId}
                                  className="h-10 rounded-md border border-input bg-background px-3"
                              />
                              <label className="block text-sm font-medium" htmlFor={cardCvvId}>
                                  CVV
                              </label>
                              <div
                                  id={cardCvvId}
                                  className="h-10 rounded-md border border-input bg-background px-3"
                              />
                              <label
                                  className="block text-sm font-medium"
                                  htmlFor={cardPostalCodeId}
                              >
                                  Card billing ZIP
                              </label>
                              <div
                                  id={cardPostalCodeId}
                                  className="h-10 rounded-md border border-input bg-background px-3"
                              />
                          </div>,
                          hostedDocument.body
                      )}
            </iframe>
        </div>
    );
}
